import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { Person } from "@/Common/Models/Person/Person";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { DocumentUIState } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";
import { BatchScanResponse } from "@/Documents/Api/Dto/Response/BatchScanResponse";
import { DocumentUrlAndBool } from "@/Documents/Api/Dto/Response/DocumentUrlAndBool";
import { LoadMedicalDocuments } from "@/Documents/Api/Dto/Response/LoadMedicalDocuments";
import { MedicalDocumentsSearch } from "@/Documents/Api/Dto/Response/MedicalDocumentSearch";
import { MedicalDocumentItem, MedicalDocumentItemDto } from "@/Documents/Models/Medical/MedicalDocumentItem";
import { MedicalDocumentTableItem } from "@/Documents/Models/Medical/MedicalDocumentTableItem";
import { DocumentCreateEditUIModel } from "@/Documents/Models/UI/IDocumentCreateEditUIModel";
import { DocumentsStore } from "@/Documents/Stores/DocumentsStore";
import { fetchPrescriptionsRenewalCounters } from "@/PrescriptionRenewals/Api/PrescriptionRenewalsClient";

class DocumentsResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);
        this.RegisterOperation(ServerOperations.Server.Myle_MedicalDocuments_CreateNote, this.createNoteFromForm, SubServerOperations.Documents.CreateNoteFromForm);
        this.RegisterOperation(ServerOperations.Server.Myle_MedicalDocuments_ApproveDocuments, this.approveDocumentsGrid, SubServerOperations.Documents.ApproveFromGrid);
        this.RegisterOperation(ServerOperations.Server.Myle_MedicalDocuments_ApproveDocuments, this.approveDocumentsDoc, SubServerOperations.Documents.ApproveFromDocument);
        this.RegisterOperation(ServerOperations.Server.Myle_MedicalDocuments_Search, this.search);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadDocument, this.openDocument, SubServerOperations.Documents.DisplayDocumentView);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadDocument, this.openDocumentWithAnnotations, SubServerOperations.Documents.DisplayDocumentAnnotationView);
        this.RegisterOperation(ServerOperations.Server.Myle_UpdateUnapprouvedDocumentMetadataRequestResponse, this.updateDocument);
        this.RegisterOperation(ServerOperations.Server.Myle_MedicalDocument_SaveApprovedDocument, this.saveApprovedDocument);
        this.RegisterOperation(ServerOperations.Server.Myle_UpdateApprouvedDocumentMetadataRequestResponse, this.onDocumentMetadataFromNotePopupResponse);

        this.RegisterOperation(ServerOperations.Server.Myle_MedicalDocuments_CheckIfHasBatchScan, this.checkIfHasBatchScan);
        this.RegisterOperation(ServerOperations.Server.Myle_MedicalDocuments_StartNewBatchProject, this.startNewBatchProject);
        this.RegisterOperation(ServerOperations.Server.Myle_SplitPdf_Proxy_SplitPDF, this.splitPDFForBatchScan);
        this.RegisterOperation(ServerOperations.Server.Myle_MedicalDocuments_GetBatchScanPagesInfo, this.setBatchScanPages);
        this.RegisterOperation(ServerOperations.Server.Myle_MedicalDocuments_AttachDocumentFromBatchScan, this.attachDocumentFromBatchScan);

        this.RegisterOperation(ServerOperations.Server.Myle_MedicalDocuments_RemovePage, this.removePage);
        this.RegisterOperation(ServerOperations.Server.Myle_MedicalDocuments_RotatePage, this.rotatePage);

        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem, this.sendDocumentToPortal);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_RevokeSharedItem, this.sendDocumentToPortal);

        this.RegisterOperation(ServerOperations.Server.Myle_MedicalDocuments_RevokeApproval, this.unapproveDocument);

        this.RegisterOperation(ServerOperations.Server.Myle_LoadAttachedCommunications, this.loadAttachedCommunications, SubServerOperations.AttachedCommunications.Documents);
    }

    private createNoteFromForm(response: ServerEvent, store: DocumentsStore): void {
        let doc = store.DocumentsModel.get("SelectedDocument");
        doc = doc.set("ShowNoteCreateForm", false);
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", doc);
        store.DocumentActions.SearchDocuments(store.DocumentsModel);
    }

    private approveDocumentsDoc(response: ServerEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocumentId", null);
        store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.Search);
        // on create note received requery medicaldocuments
        store.DocumentActions.SearchDocuments(store.DocumentsModel);
        store.DocumentActions.setUnmanagedDocumentCount(store.DocumentsModel);

        if (response.hasErrors()) {
            const errors = response.Errors[0].Message?.split("\\n");
            sessionStore.showPopup(errors);
        }
    }

    private approveDocumentsGrid(response: ServerEvent, store: DocumentsStore): void {
        // on create note received requery medicaldocuments

        // this can be done more efficiently
        //  if (store.DocumentsModel.get(
        store.DocumentActions.SearchDocuments(store.DocumentsModel);
        store.DocumentActions.setUnmanagedDocumentCount(store.DocumentsModel);

        if (response.hasErrors()) {
            const errors = response.Errors[0].Message?.split("\\n");
            sessionStore.showPopup(errors);
        }
    }

    private async search(response: ServerEvent, store: DocumentsStore): Promise<void> {
        if (response.hasErrors()) {
            store.DocumentsModel = store.DocumentsModel
                .set("SearchOptionsOpen", false)
                .set("IsLoadingFromServer", false);
            sessionStore.showPopup(response.Errors);
            return;
        }

        var searchResponse: MedicalDocumentsSearch = response.Data;

        var medicalDocumentTableItems = searchResponse.MedicalDocumentList.map(doc => MedicalDocumentTableItem.FromMedicalDocumentItemResponse(doc));

        store.DocumentsModel = store.DocumentsModel.set("SearchOptionsOpen", false);
        store.DocumentsModel = store.DocumentsModel.set("DocumentsTableData", medicalDocumentTableItems.toImmutable());
        store.DocumentsModel = store.DocumentsModel.set("TotalQueryPages", searchResponse.TotalPages);
        store.DocumentsModel = store.DocumentsModel.set("TotalQueryRows", searchResponse.TotalRows);
        store.DocumentsModel = store.DocumentsModel.set("CurrentPage", searchResponse.CurrentPage);
        store.DocumentsModel = store.DocumentsModel.set("IsLoadingFromServer", false);

        // update the currently selected document if there is one
        if (store.DocumentsModel.get("SelectedDocument")) {
            const selectedDocumentId = store.DocumentsModel.get("SelectedDocumentId");
            const updatedMedicalInfo = searchResponse.MedicalDocumentList.firstOrDefault(item => item.Id === selectedDocumentId);

            if (updatedMedicalInfo) {
                const selectedDocument = store.DocumentsModel.get("SelectedDocument").set("MedicalDocumentInfo", new MedicalDocumentItem(updatedMedicalInfo));
                store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", selectedDocument);
            }
        }

        store.DocumentActions.setUnmanagedDocumentCount(store.DocumentsModel);
        sessionStore.hideGlobalOverlay();
    }

    private openDocument(response: ServerEvent, store: DocumentsStore): void {
        this.loadDocument(response, store);
        store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.View);
    }

    private openDocumentWithAnnotations(response: ServerEvent, store: DocumentsStore): void {
        this.loadDocument(response, store);
        store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.Annotate);
    }

    private loadDocument(response: ServerEvent, store: DocumentsStore): void {
        let docResponse: LoadMedicalDocuments = response.Data;

        if (docResponse.MedicalDocumentListItem != null && docResponse.MedicalDocumentListItem.length > 0) {
            const medicalDocumentListItemResponse = docResponse.MedicalDocumentListItem[0];
            const selectedDocument = DocumentCreateEditUIModel.fromResponseModel(medicalDocumentListItemResponse);

            store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", selectedDocument);
            store.DocumentsModel = store.DocumentsModel.set("CurrentAttachements", medicalDocumentListItemResponse.DocumentPathList.toImmutable());
        }
    }

    private updateDocument(response: ServerEvent, store: DocumentsStore): void {
        store.DocumentActions.LoadDocument(response.Data["DocumentId"]);
        store.DocumentActions.setUnmanagedDocumentCount(store.DocumentsModel);
    }

    private saveApprovedDocument(response: ServerEvent, store: DocumentsStore): void {
        let documentView = store.DocumentsModel.get("SelectedDocument");
        documentView = documentView.set("LastModifiedBy", response.Data["ModifiedById"]);
        documentView = documentView.set("LastModifiedByDate", response.Data["ModifiedByDate"]);
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", documentView);

        let docs = store.DocumentsModel.get("DocumentsTableData").toArray();
        var currentDoc: MedicalDocumentTableItem = ArrayHelper.Find(docs, x => x.Model.Model.Id == documentView.get("MedicalDocumentInfo").Model.Id);
        if (currentDoc != null) {
            let patient = currentDoc.Model.Model.Patients;

            currentDoc.Model.Model = $.extend({}, true, documentView.get("MedicalDocumentInfo").Model);
            currentDoc.Model.Model.Patients = patient;
            currentDoc.Model.Model.SharedItem = response.Data["SharedItem"];

            var idx = currentDoc != null ? docs.indexOf(currentDoc) : -1;
            docs[idx] = currentDoc;
            store.DocumentsModel = store.DocumentsModel.set("DocumentsTableData", Immutable.List(docs));
        }

        store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.Search);
        // Upload Search documents
        store.DocumentActions.SearchDocuments(store.DocumentsModel);
    }

    private onDocumentMetadataFromNotePopupResponse(response: ServerEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel.set("NoteWindowOpen", false);
        const updatedMedicalDocumentItem = response.Data["MedicalDocumentItem"] as MedicalDocumentItemDto;

        const docs = store.DocumentsModel.get("DocumentsTableData").toArray();
        const currentDoc: MedicalDocumentTableItem = ArrayHelper.Find(docs, item => item.Model.Model.Id == updatedMedicalDocumentItem.Id);
        if (currentDoc != null) {
            currentDoc.Model = new MedicalDocumentItem(updatedMedicalDocumentItem);
            const selectedClinicians = currentDoc.Model.Model.ResponsibleProviders.map(provider => cacheStore.GetClinicians(true).firstOrDefault(contact => contact.ContactFacetId === provider.Id || contact.ContactId === provider.Id));
            let selectedDocument = store.DocumentsModel.get("SelectedDocument");
            selectedDocument = selectedDocument.set("SelectedClinicians", Immutable.List<Person>(selectedClinicians));
            selectedDocument = selectedDocument.set("MedicalDocumentInfo", currentDoc.Model);
            store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", selectedDocument);
            store.DocumentsModel = store.DocumentsModel.set("DocumentsTableData", Immutable.List(docs));
        }

        if (documentsStore.DocumentsModel.get("UIState") === DocumentUIState.Search) {
            store.DocumentActions.SearchDocuments(store.DocumentsModel);
            $("table.myle-table tbody tr").removeClass("active"); // clear selected rows
        }

        sessionStore.hideGlobalOverlay();
    }

    private sendDocumentToPortal(response: ServerEvent, store: DocumentsStore): void {
        let docSyncModel = store.DocumentsModel.get("PortalLinkedItemModel");

        if (response.hasErrors()) {
            docSyncModel = docSyncModel.set("ErrorMessage", response.Errors[0].Message);
        }
        else {
            let docId = response.Data["ObjectId"];
            let sharedItem: SharedItem = response.Data["SharedItem"];

            let docs = store.DocumentsModel.get("DocumentsTableData").toArray();
            docs.forEach(x => {
                if (x.Model.Model.Id == docId)
                    x.Model.Model.SharedItem = sharedItem;
            });
            store.DocumentsModel = store.DocumentsModel.set("DocumentsTableData", docs.toImmutable());

            docSyncModel = docSyncModel.set("SharedItem", sharedItem);
            store.DocumentsModel = store.DocumentsModel.set("IsSendToPatientPortalPopupOpen", false);

            // if we are in view mode, reload document
            const uiState = store.DocumentsModel.get("UIState");
            if (uiState === DocumentUIState.View || uiState === DocumentUIState.Annotate)
                store.DocumentActions.LoadDocument(docId, uiState === DocumentUIState.Annotate);
            else { // is it a good way to load note?! or is better to load notes in upsert sharedItem??!!!
                let doc = store.DocumentsModel.get("SelectedDocument");
                doc = doc.set("ShowNoteCreateForm", false);
                store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", doc);
                store.DocumentActions.SearchDocuments(store.DocumentsModel);
            }

            sessionStore.hideGlobalOverlay();
        }

        docSyncModel = docSyncModel.set("IsSending", false);
        store.DocumentsModel = store.DocumentsModel.set("PortalLinkedItemModel", docSyncModel);
    }

    private checkIfHasBatchScan(response: ServerEvent, store: DocumentsStore): void {
        let hasBatchScan: boolean = response.Data["HasBatchScanned"];

        let documentCreateModel = store.DocumentsModel.get("NewDocument");
        documentCreateModel = documentCreateModel.set("HasBatchScan", hasBatchScan);

        store.DocumentsModel = store.DocumentsModel.set("NewDocument", documentCreateModel);
    }

    private startNewBatchProject(response: ServerEvent, store: DocumentsStore): void {
        let documentCreateModel = store.DocumentsModel.get("NewDocument");
        documentCreateModel = documentCreateModel.set("HasBatchScan", false);

        store.DocumentsModel = store.DocumentsModel.set("NewDocument", documentCreateModel);
    }

    private splitPDFForBatchScan(response: ServerEvent, store: DocumentsStore): void {
        // To be removed, with all chained calls in next PRzs
        if (response.Data["Success"]) {
            let documentCreateModel = store.DocumentsModel.get("NewDocument");

            store.DocumentsModel = store.DocumentsModel.set("NewDocument", documentCreateModel);

            store.DocumentActions.VerifyHasBatchScan();
            store.DocumentActions.GetBatchScanPages();
        }
        else {
            console.error("Service broken", response.Errors);

            let docCreate = store.DocumentsModel.get("NewDocument");
            docCreate = docCreate.set("PleaseWaitPopup", false);
            store.DocumentsModel = store.DocumentsModel.set("NewDocument", docCreate);

            sessionStore.hideGlobalOverlay();
        }
    }

    private setBatchScanPages(response: ServerEvent, store: DocumentsStore): void {
        let batchScanPages = response.Data.BatchScanPages;
        let newDoc = store.DocumentsModel.get("NewDocument");

        let base64Array: BatchScanResponse[] = response.Data["Base64Array"];

        if (base64Array && base64Array.length > 0) {
            let base64List = base64Array.map((value: BatchScanResponse) => {
                let newImage = new DocumentUrlAndBool();
                newImage.IsChecked = false;
                newImage.PageSizes = value.PageBytes / 1000;
                newImage.PageNumber = value.PageNumber;

                return newImage;
            });

            newDoc = newDoc.set("ImageBase64List", Immutable.List(base64List));
        }

        newDoc = newDoc
            .set("BatchScanPages", batchScanPages)
            .set("PleaseWaitPopup", false)
            .set("HideBatchScanAndAttachButton", true);

        store.DocumentsModel = store.DocumentsModel
            .set("LeftMenuOpen", false)
            .set("NewDocument", newDoc);

        overlayStore.hideAllOverlays();
    }

    private attachDocumentFromBatchScan(response: ServerEvent, store: DocumentsStore): void {
        if (!response.Data.Success || response.hasErrors()) {
            sessionStore.showPopup(response.Errors);
            const newDoc = store.DocumentsModel.get("NewDocument");
            store.DocumentsModel = store.DocumentsModel.set("NewDocument", newDoc.set("LockUI", false));
            return;
        }

        let id = response.Data["DocumentId"];
        let doc = store.DocumentsModel.get("NewDocument");
        let mdoc = doc.get("MedicalDocumentInfo");
        mdoc.Model.IsUrgent = false;
        mdoc.Model.Id = id;

        const selectedPages = [...(doc.get("SelectedBatchScanPages") || [])];
        const pages = [...(doc.get("BatchScanPages") || [])];

        for (let selectedPage of selectedPages) {
            pages.firstOrDefault(page => page.PageNumber === selectedPage.pageNumber).IsIndexed = true;
        }

        const updatedPages = pages.any(page => !page.IsIndexed)
            ? pages
            : null;

        if (!updatedPages)
            doc = doc.set("CompleteBatchScan", true);

        doc = doc
            .set("MedicalDocumentInfo", mdoc)
            .set("Note", "")
            .set("SelectedBatchScanPages", [])
            .set("SelectedPageArray", Immutable.List([]))
            .set("SelectedPageTotalKb", 0)
            .set("LockUI", false)
            .set("AdditionalDetailsVisible", false)
            .set("IsUrgent", false)
            .set("SendingFacilityId", "")
            .set("SendingFacilityReport", "")
            .set("AccompanyingSubClass", "")
            .set("AccompanyingMnemonic", "")
            .set("SourceAuthorName", "")
            .set("AuthoringDate", null)
            .set("ObservationDate", null)
            .set("SelectedHRMStatus", doc.get("HRMStatusList").get(0).Key)
            .set("BatchScanPages", updatedPages);

        if (!doc.get("RememberClinician"))
            doc = doc.set("SelectedClinicians", Immutable.List([]));

        if (!doc.get("RememberDescription"))
            doc = doc.set("Description", "");

        if (!doc.get("RememberPatient") && !store.DocumentsModel.get("IsFromPatient"))
            doc = doc.set("SelectedPatients", Immutable.List([]));

        if (doc.get("RememberSite") === false) {
            const medicalDocument = doc.get("MedicalDocumentInfo");
            medicalDocument.Model.SiteId = "";
            doc = doc.set("MedicalDocumentInfo", medicalDocument);
        }

        if (response.Data.Success && response.Data.Pages) {
            let pagesToRemove: number[] = response.Data.Pages;
            let images = doc.get("ImageBase64List").toArray();

            pagesToRemove.forEach(p => images = images.filter(c => c.PageNumber !== p));

            doc = doc.set("ImageBase64List", images.toImmutable());
        }

        if (doc.get("CompleteBatchScan")) {
            doc = doc
                .set("HasBatchScan", false)
                .set("ImageBase64List", Immutable.List([]))
                .set("HideBatchScanAndAttachButton", false)
                .set("SelectedBatchScanPages", [])
                .set("SelectedPageArray", Immutable.List([]))
                .set("SelectedPageTotalKb", 0)
                .set("CompleteBatchScan", false);

            store.DocumentsModel = store.DocumentsModel.set("LeftMenuOpen", true);

            store.DocumentActions.StartNewBatchPojectClick();
        }

        store.DocumentsModel = store.DocumentsModel.set("NewDocument", doc);
        store.DocumentActions.setUnmanagedDocumentCount(store.DocumentsModel);

        window.setTimeout(() => {
            let ev = new LocalEvent(LocalOperations.Misc.Focus);
            ev.AddItem("id", "add_doc_patients");
            MiscDispatcher.dispatch(ev);
        }, 100);
    }

    private removePage(response: ServerEvent, store: DocumentsStore): void {
        if (response.Data.Success) {
            let pageNumber: number = response.Data.PageNumber;

            let doc = store.DocumentsModel.get("NewDocument");
            let pages = doc.get("BatchScanPages");

            pages[pageNumber - 1].IsIndexed = true;

            const updatedPages = pages.any(page => !page.IsIndexed)
                ? [...pages]
                : null;

            const updatedSelectedPages = (doc.get("SelectedBatchScanPages") || []).filter(p => p.pageNumber !== response.Data.PageNumber);
            let selectedPages: number[] = [];
            let totalKb = 0;

            let images = doc.get("ImageBase64List").toArray();

            images = images.filter(i => i.PageNumber !== pageNumber);
            images.forEach(x => {
                if (x.IsChecked) {
                    totalKb += x.PageSizes;
                    selectedPages.push(x.PageNumber);
                }
            });

            doc = doc.set("SelectedPageArray", Immutable.List(selectedPages))
                .set("SelectedPageTotalKb", totalKb)
                .set("ImageBase64List", images.toImmutable())
                .set("BatchScanPages", updatedPages)
                .set("SelectedBatchScanPages", updatedSelectedPages);

            store.DocumentsModel = store.DocumentsModel.set("NewDocument", doc);
        }
    }

    private rotatePage(response: ServerEvent, store: DocumentsStore): void {
        if (response.Data.hasErrors) {
            return;
        }

        let pageNumber: number = response.Data.PageNumber;

        let doc = store.DocumentsModel.get("NewDocument");
        let pages = doc.get("BatchScanPages");
        pages[pageNumber - 1].Rotation = response.Data.Orientation;
        doc = doc.set("BatchScanPages", [...pages]);

        store.DocumentsModel = store.DocumentsModel.set("NewDocument", doc);
    }

    private unapproveDocument(response: ServerEvent, store: DocumentsStore): void {
        if (response.Data["IsSuccess"] === true) {
            let doc = store.DocumentsModel.get("SelectedDocument")
                .set("UnapprovePopupOpen", !store.DocumentsModel.get("SelectedDocument").get("UnapprovePopupOpen"))
                .set("SignOffByContactId", null)
                .set("SignOffDate", null);
            const medInfo = doc.get("MedicalDocumentInfo");
            let model = medInfo.Model;
            model.SignOffByContactId = null;
            model.SignOffDate = null;
            doc = doc.set("MedicalDocumentInfo", medInfo);

            store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", doc);

            sessionStore.hideGlobalOverlay();

            if (doc.get("MedicalDocumentInfo").Model?.PrescriptionRenewal) {
                fetchPrescriptionsRenewalCounters();
            }
        }
    }

    private loadAttachedCommunications(response: ServerEvent, store: DocumentsStore): void {
        if (response.Data.Success) {
            let selectedDocument = store.DocumentsModel.get("SelectedDocument");
            let medInfo = selectedDocument.get("MedicalDocumentInfo");
            let model = medInfo.Model;

            model.AreAllTasksCompleted = response.Data.AreAllTasksCompleted;
            model.IndividualTaskLinkedItems = response.Data.IndividualTaskLinkedItems;

            selectedDocument = selectedDocument.set("MedicalDocumentInfo", new MedicalDocumentItem(medInfo.Model));
            store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", selectedDocument);
        }
    }
}

export { DocumentsResponseHandler };
